import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft, Plus, Trash2 } from 'lucide-react'
import { api } from '@/lib/api'

interface LostReason {
  id: string
  name: string
  description: string | null
  active: boolean
  position: number
}

export function LostReasonsPage() {
  const [items, setItems] = useState<LostReason[]>([])
  const [err, setErr] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [newName, setNewName] = useState('')
  const [newDesc, setNewDesc] = useState('')

  async function load() {
    setErr(null)
    try {
      const r = await api<{ items: LostReason[] }>('/v1/marketing/lost-reasons')
      setItems(r.items)
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    }
  }

  useEffect(() => {
    void load()
  }, [])

  async function create() {
    if (!newName.trim()) return
    try {
      await api('/v1/marketing/lost-reasons', {
        method: 'POST',
        body: JSON.stringify({ name: newName.trim(), description: newDesc.trim() || undefined }),
      })
      setNewName('')
      setNewDesc('')
      setCreating(false)
      void load()
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    }
  }

  async function toggleActive(r: LostReason) {
    try {
      await api(`/v1/marketing/lost-reasons/${r.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ active: !r.active }),
      })
      void load()
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    }
  }

  async function remove(r: LostReason) {
    if (!confirm(`Eliminar "${r.name}"? Las oportunidades que la referencian quedarán sin razón.`)) {
      return
    }
    try {
      await api(`/v1/marketing/lost-reasons/${r.id}`, { method: 'DELETE' })
      void load()
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    }
  }

  return (
    <div className="p-6 max-w-3xl">
      <Link
        to="/marketing/pipeline"
        className="text-blueprint-700 text-xs flex items-center gap-1 mb-2"
      >
        <ArrowLeft className="size-3" /> Pipeline
      </Link>
      <header className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-lg font-medium tracking-tight">Razones de pérdida</h1>
          <p className="text-xs font-mono text-ink-700/60">
            catálogo · usado al cerrar opp como perdida · evita texto libre para conversion analytics
          </p>
        </div>
        {!creating && (
          <button onClick={() => setCreating(true)} className="bp-btn-primary">
            <Plus className="size-3" /> Nueva razón
          </button>
        )}
      </header>

      {err && (
        <div className="mb-3 text-xs font-mono text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
          {err}
        </div>
      )}

      {creating && (
        <div className="bp-card p-4 mb-4 space-y-2">
          <div>
            <label className="text-[10px] font-mono uppercase text-ink-700/60">Nombre</label>
            <input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              className="w-full border border-ink-900/15 rounded px-2 py-1.5 text-sm"
              placeholder="Sin presupuesto"
            />
          </div>
          <div>
            <label className="text-[10px] font-mono uppercase text-ink-700/60">
              Descripción (opcional)
            </label>
            <input
              value={newDesc}
              onChange={(e) => setNewDesc(e.target.value)}
              className="w-full border border-ink-900/15 rounded px-2 py-1.5 text-sm"
            />
          </div>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => {
                setCreating(false)
                setNewName('')
                setNewDesc('')
              }}
              className="bp-btn"
            >
              Cancelar
            </button>
            <button onClick={create} className="bp-btn-primary">
              Crear
            </button>
          </div>
        </div>
      )}

      <div className="bp-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-surface-alt text-xs font-mono uppercase tracking-wider text-ink-700/60">
            <tr>
              <th className="px-3 py-2 text-left">Nombre</th>
              <th className="px-3 py-2 text-left">Descripción</th>
              <th className="px-3 py-2 text-left">Estado</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((r) => (
              <tr key={r.id} className="border-t border-ink-900/10">
                <td className="px-3 py-2 font-medium">{r.name}</td>
                <td className="px-3 py-2 text-xs text-ink-700/70">{r.description ?? '—'}</td>
                <td className="px-3 py-2">
                  <button
                    onClick={() => toggleActive(r)}
                    className={[
                      'bp-pill',
                      r.active
                        ? 'bg-emerald-50 text-emerald-700'
                        : 'bg-ink-900/10 text-ink-700/60',
                    ].join(' ')}
                  >
                    {r.active ? 'activa' : 'inactiva'}
                  </button>
                </td>
                <td className="px-3 py-2 text-right">
                  <button
                    onClick={() => remove(r)}
                    className="text-red-700 hover:text-red-900"
                    title="Eliminar"
                  >
                    <Trash2 className="size-3.5" />
                  </button>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr>
                <td colSpan={4} className="px-3 py-8 text-center text-xs text-ink-700/50">
                  Sin razones todavía.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
